import { useEffect, useState } from 'react';
import { Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import { fetchActivity, verbLabel, type ActivitySnapshot } from '../api/activity';
import { t } from '../lib/i18n';

/**
 * Live "who is running what" panel for the admin users page. The Pacesetter
 * daemon answers ONE query at a time, so one user's heavy pull (a wide report,
 * a 30-day range) slows everyone; this panel makes that visible and defensible
 * — the admin sees "usuario X lleva 40s corriendo un reporte" instead of
 * fielding "el sistema está caído". Polls every 5s while mounted.
 */
export default function ActivityPanel() {
  const [snap, setSnap] = useState<ActivitySnapshot | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = () => {
      fetchActivity()
        .then((s) => { if (alive) { setSnap(s); setErr(false); } })
        .catch(() => { if (alive) setErr(true); });
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (err || snap === null) return null; // no clutter when unavailable

  const inFlight = snap.in_flight ?? [];
  const recent = (snap.recent ?? []).slice(0, 12);

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Title>{t.activity.title}</Title>
        {inFlight.length === 0
          ? <Badge color="emerald">{t.activity.idle}</Badge>
          : <Badge color="amber">{t.activity.busyCount.replace('{n}', String(inFlight.length))}</Badge>}
      </div>
      <Text className="text-tremor-content text-sm">{t.activity.subtitle}</Text>

      {inFlight.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.activity.colUser}</TableHeaderCell>
                <TableHeaderCell>{t.activity.colWhat}</TableHeaderCell>
                <TableHeaderCell>{t.activity.colRunning}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inFlight.map((r, i) => (
                <TableRow key={`f${i}`}>
                  <TableCell>{r.user}</TableCell>
                  <TableCell title={r.params}>{verbLabel(r.verb)}</TableCell>
                  <TableCell>
                    <Badge color={(r.running_ms ?? 0) > 10000 ? 'rose' : 'amber'}>
                      {Math.round((r.running_ms ?? 0) / 1000)} s
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-4">
          <Text className="text-sm font-medium">{t.activity.recentTitle}</Text>
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.activity.colUser}</TableHeaderCell>
                  <TableHeaderCell>{t.activity.colWhat}</TableHeaderCell>
                  <TableHeaderCell>{t.activity.colTook}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map((r, i) => (
                  <TableRow key={`r${i}`}>
                    <TableCell>{r.user}</TableCell>
                    <TableCell title={r.params}>{verbLabel(r.verb)}</TableCell>
                    <TableCell>
                      <span className={!r.ok ? 'text-rose-500' : (r.ms ?? 0) > 10000 ? 'text-amber-500' : ''}>
                        {((r.ms ?? 0) / 1000).toFixed(1)} s{!r.ok ? ` · ${t.activity.failed}` : ''}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Card>
  );
}
